import React from 'react';
import { Helmet } from 'react-helmet';
import { Box } from 'rebass';
import { useParams } from 'react-router-dom';

import AppPageWrapper from 'components/AppPageWrapper';
import AppPageWrapperSectionHeader from 'components/AppPageWrapper/AppPageWrapperSectionHeader';
import AppPageWrapperSectionBody from 'components/AppPageWrapper/AppPageWrapperSectionBody';
import BreadcrumbBack from 'components/BreadcrumbBack';
import useQuery from 'hooks/useQuery';
import H1 from 'components/H1';
import { Skeleton } from 'components/Skeleton';
import services from 'services/services';
import { useToastHelper } from 'hooks/useToastHelper';

/**
 * Branding Layout
 *
 * @param {React.Children} children
 * @param {import("/services/services.js").Branding} [branding]
 * @param {boolean} [isLoading]
 * @returns {JSX.Element}
 */
export function BrandingDetailsLayout({ children, branding, isLoading }) {
    return (
        <AppPageWrapper>
            <Helmet>
                <title>Branding Settings</title>
            </Helmet>
            <AppPageWrapperSectionHeader className="experience experience-border experience-no-subnav section-header-sticky">
                <Box>
                    <BreadcrumbBack to="/settings/brandings">Back to all brandings</BreadcrumbBack>
                    {isLoading && !branding && <Skeleton width={300} height={38} />}
                    {!isLoading && branding && <H1>{branding.name} Settings</H1>}
                    {!isLoading && !branding && <H1>Not Found</H1>}
                </Box>
            </AppPageWrapperSectionHeader>
            <AppPageWrapperSectionBody css={{ display: 'block' }}>{children}</AppPageWrapperSectionBody>
        </AppPageWrapper>
    );
}

export const withBrandingDetailsLayout = Component => props => {
    const { brandingId } = useParams();
    const { addErrorToast } = useToastHelper();
    const { isLoading, data: branding, refetch } = useQuery({
        queryFn: variables => services.getBranding(variables.brandingId),
        variables: { brandingId },
        onError: (_, errorMessage) => addErrorToast(errorMessage)
    });

    return (
        <BrandingDetailsLayout isLoading={isLoading} branding={branding}>
            <Component {...props} isLoading={isLoading} branding={branding} refetchBranding={refetch} />
        </BrandingDetailsLayout>
    );
};
